import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApiService } from '../../services/adminApi';
import { usePageClamp } from '../../hooks/usePageClamp';
import { useSearchDraft } from '../../hooks/useSearchDraft';
import { urlEnum, urlOptionalId, urlPositiveInt, urlString, useUrlState } from '../../hooks/useUrlState';
import { SectionFilterBar, TableCard, TableHeader, TableViewport, usePageSize } from '../table/TableKit';
import {
  Badge,
  EmptyRow,
  ErrorNotice,
  InstanceSelect,
  LoadingSpinner,
  SortableTh,
  instanceLabel,
  selectClass,
  tdClass,
  thClass,
} from './shared';
import RelativeTime from '../RelativeTime';
import { maskApiKey } from '../../utils/apiKey';

const URL_SPEC = {
  instance: urlOptionalId(),
  status: urlEnum(['', 'active', 'revoked'] as const, ''),
  q: urlString(),
  sort: urlEnum(['name', 'api_key_prefix', 'instance_name', 'is_active', 'created_at', 'last_used_at', 'revoked_at'] as const, 'created_at'),
  order: urlEnum(['asc', 'desc'] as const, 'desc'),
  page: urlPositiveInt(),
};

function AdminApiKeys() {
  const [pageSize, setPageSize] = usePageSize('admin-api-keys');
  const [state, setState] = useUrlState(URL_SPEC);
  const [searchInput, setSearchInput] = useSearchDraft(state.q, (q) => setState({ q, page: 1 }));
  const offset = (state.page - 1) * pageSize;

  const params = useMemo(
    () => ({
      limit: pageSize,
      offset,
      instance_id: state.instance,
      status: state.status || undefined,
      search: state.q || undefined,
      sort: state.sort,
      order: state.order,
    }),
    [pageSize, offset, state.instance, state.status, state.q, state.sort, state.order],
  );

  const { data, isLoading, isPlaceholderData, error } = useQuery({
    queryKey: ['admin', 'apiKeys', params],
    queryFn: () => adminApiService.getApiKeys(params),
    placeholderData: (prev) => prev,
    retry: false,
  });

  usePageClamp({
    page: state.page,
    pageSize,
    total: data?.total,
    ready: !isLoading && !isPlaceholderData && !!data,
    setPage: (page) => setState({ page }),
  });

  const onSort = (sort: string, order: 'asc' | 'desc') => setState({
    sort: sort as typeof state.sort,
    order,
    page: 1,
  });

  return (
    <div>
      {error && <ErrorNotice message="Failed to load API keys." />}
      <SectionFilterBar>
        <InstanceSelect
          value={state.instance}
          onChange={(instance) => setState({ instance, page: 1 })}
        />
        <select
          aria-label="Status filter"
          className={selectClass}
          value={state.status}
          onChange={(e) => setState({ status: e.target.value as typeof state.status, page: 1 })}
        >
          <option value="">All Statuses</option>
          <option value="active">active</option>
          <option value="revoked">revoked</option>
        </select>
      </SectionFilterBar>
      <TableCard>
        <TableHeader
          title="API Keys"
          search={{
            value: searchInput,
            onChange: setSearchInput,
            ariaLabel: 'Search API keys',
          }}
          pagination={{
            offset,
            limit: pageSize,
            total: data?.total ?? 0,
            onOffsetChange: (nextOffset) => setState({ page: nextOffset / pageSize + 1 }),
            onLimitChange: (size) => {
              setPageSize(size);
              setState({ page: 1 });
            },
          }}
        />
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <TableViewport>
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-700">
                <SortableTh label="Name" sortKey="name" sort={state.sort} order={state.order} onSort={onSort} firstOrder="asc" />
                <SortableTh label="Prefix" sortKey="api_key_prefix" sort={state.sort} order={state.order} onSort={onSort} firstOrder="asc" />
                <SortableTh label="Instance" sortKey="instance_name" sort={state.sort} order={state.order} onSort={onSort} firstOrder="asc" />
                <th className={thClass}>Scopes</th>
                <SortableTh label="Status" sortKey="is_active" sort={state.sort} order={state.order} onSort={onSort} />
                <SortableTh label="Created" sortKey="created_at" sort={state.sort} order={state.order} onSort={onSort} />
                <SortableTh label="Last Used" sortKey="last_used_at" sort={state.sort} order={state.order} onSort={onSort} />
                <SortableTh label="Revoked At" sortKey="revoked_at" sort={state.sort} order={state.order} onSort={onSort} />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {(data?.items ?? []).map((apiKey) => (
                <tr key={apiKey.id}>
                  <td className={tdClass}>{apiKey.name}</td>
                  <td className={`${tdClass} font-mono`}>{maskApiKey(apiKey)}</td>
                  <td className={tdClass}>{instanceLabel(apiKey.instance_name, apiKey.instance_key)}</td>
                  <td className={tdClass}>
                    {apiKey.scopes.map((scope) => (
                      <span key={scope} className="bg-gray-600/30 text-gray-300 px-2 py-0.5 rounded text-xs mr-1">
                        {scope}
                      </span>
                    ))}
                  </td>
                  <td className={tdClass}><Badge value={apiKey.is_active ? 'active' : 'revoked'} /></td>
                  <td className={tdClass}><RelativeTime value={apiKey.created_at} /></td>
                  <td className={tdClass}><RelativeTime value={apiKey.last_used_at} /></td>
                  <td className={tdClass}><RelativeTime value={apiKey.revoked_at} /></td>
                </tr>
              ))}
              {(data?.items ?? []).length === 0 && <EmptyRow colSpan={8} text="No API keys found." />}
            </tbody>
          </table>
          </TableViewport>
        )}
      </TableCard>
    </div>
  );
}

export default AdminApiKeys;
